import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { api } from '../services/apiClient';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

const isSaleActive = (saleDiscountPercent, saleStartDate, saleEndDate) => {
  if (!saleDiscountPercent || saleDiscountPercent <= 0) return false;
  const now = new Date();
  if (saleStartDate && new Date(saleStartDate) > now) return false;
  if (saleEndDate && new Date(saleEndDate) < now) return false;
  return true;
};

const isSaleApplicableToTier = (saleApplicableTiers, pricingTier) => {
  if (!saleApplicableTiers || saleApplicableTiers.length === 0) return true;
  return saleApplicableTiers.includes(pricingTier);
};

// Helper function to calculate price with quantity discounts and sale
const calculatePriceWithQuantityDiscount = (tierPrices, quantity, pricingTier = 'retail', saleInfo = null) => {
  if (!tierPrices) return null;
  
  // Get base price for user's tier
  let basePrice = tierPrices.retail || 0;
  if (pricingTier === 'wholesale' && tierPrices.wholesale) {
    basePrice = tierPrices.wholesale;
  } else if (pricingTier === 'volume' && tierPrices.volume) {
    basePrice = tierPrices.volume;
  }
  
  // If sale is active for this tier, sale takes precedence over quantity discounts
  if (saleInfo && isSaleActive(saleInfo.sale_discount_percent, saleInfo.sale_start_date, saleInfo.sale_end_date)
      && isSaleApplicableToTier(saleInfo.sale_applicable_tiers, pricingTier)) {
    return basePrice * (1 - saleInfo.sale_discount_percent / 100);
  }
  
  // Check if quantity discounts exist
  const quantityDiscounts = tierPrices.quantity_discounts;
  if (!quantityDiscounts || !quantityDiscounts.breaks || quantityDiscounts.breaks.length === 0) {
    return basePrice;
  }
  
  // Find the best qualifying discount break
  let bestBreak = null;
  const sortedBreaks = [...quantityDiscounts.breaks].sort((a, b) => b.min_quantity - a.min_quantity);
  
  for (const breakItem of sortedBreaks) {
    if (quantity < breakItem.min_quantity) continue;
    
    if (breakItem.applicable_tiers && breakItem.applicable_tiers.length > 0) {
      if (!breakItem.applicable_tiers.includes(pricingTier)) {
        continue;
      }
    }
    
    bestBreak = breakItem;
    break;
  }
  
  if (bestBreak) {
    if (bestBreak.price_override !== null && bestBreak.price_override !== undefined) {
      return bestBreak.price_override;
    } else if (bestBreak.discount_percent !== null && bestBreak.discount_percent !== undefined) {
      return basePrice * (1 - bestBreak.discount_percent / 100);
    }
  }
  
  return basePrice;
};

// The key cart screens use for a line (removeItem / updateQuantity take it):
// server lines have cart_id/product_id, guest lines cartId/id.
export const cartItemKey = (item) => item.cartId || item.cart_id || item.id || item.product_id;

const subcategoryIdOf = (item) =>
  (item.subcategory && typeof item.subcategory === 'object' ? item.subcategory.id : item.subcategory) || null;

// Wait for quick successive cart changes to settle before re-checking.
const AVAILABILITY_CHECK_DELAY_MS = 300;

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  // Whether each line can still be bought, keyed by cartItemKey (see the
  // availability effect below).
  const [availability, setAvailability] = useState({});
  const [availabilityCheck, setAvailabilityCheck] = useState(0);
  const latestAvailabilityCheck = useRef(0);
  const { user, isAuthenticated } = useAuth();

  // Ask the server whether every line can still be bought — the product is
  // still active, the chosen option and add-ons are still offered, and
  // there's enough stock — whenever the cart changes or is opened. Carts can
  // sit for months; checkout refuses unavailable lines either way, this just
  // tells the shopper first.
  useEffect(() => {
    if (isSyncing) return undefined;
    const check = latestAvailabilityCheck.current + 1;
    latestAvailabilityCheck.current = check;
    if (cartItems.length === 0) {
      setAvailability({});
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await api.cart.checkAvailability(cartItems.map((item) => ({
          key: cartItemKey(item),
          product_id: item.product_id || item.id,
          quantity: Math.max(1, Number(item.quantity) || 1),
          subcategory_id: subcategoryIdOf(item),
          selected_components: (item.selected_components || []).map((component) => ({
            id: component.id,
            variation_id: component.variation_id || null,
          })),
        })));
        if (check !== latestAvailabilityCheck.current) return;
        setAvailability(Object.fromEntries(result.items.map((status) => [status.key, status])));
      } catch (err) {
        // Couldn't check: don't hold the shopper up over it (checkout still checks).
        if (check === latestAvailabilityCheck.current) setAvailability({});
      }
    }, AVAILABILITY_CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [cartItems, isSyncing, availabilityCheck]);

  const recheckAvailability = useCallback(() => setAvailabilityCheck((count) => count + 1), []);

  // Load cart from localStorage on mount (for non-authenticated users)
  useEffect(() => {
    if (!isAuthenticated) {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        try {
          setCartItems(JSON.parse(savedCart));
        } catch (err) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error loading cart:', err);
          }
        }
      }
    }
  }, [isAuthenticated]);

  // Save cart to localStorage for non-authenticated users
  useEffect(() => {
    if (!isAuthenticated && !isSyncing) {
      localStorage.setItem('cart', JSON.stringify(cartItems));
    }
  }, [cartItems, isAuthenticated, isSyncing]);

  // Sync cart with backend when user logs in
  useEffect(() => {
    const syncCartWithBackend = async () => {
      if (isAuthenticated && user) {
        setIsSyncing(true);
        try {
          // Get local cart from localStorage
          const localCartJson = localStorage.getItem('cart');
          const localCartItems = localCartJson ? JSON.parse(localCartJson) : [];
          
          if (localCartItems.length > 0) {
            // Sync local cart with server
            const syncedCart = await api.cart.sync({ items: localCartItems });
            setCartItems(syncedCart.items);
            // Clear localStorage after successful sync
            localStorage.removeItem('cart');
            // Recalculate prices with quantity discounts
            await recalculateCartPrices();
          } else {
            // Just fetch server cart
            const serverCart = await api.cart.get();
            setCartItems(serverCart.items);
            // Recalculate prices with quantity discounts
            await recalculateCartPrices();
          }
        } catch (err) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error syncing cart:', err);
          }
          // If sync fails, keep local cart
        } finally {
          setIsSyncing(false);
        }
      }
    };

    syncCartWithBackend();
  }, [isAuthenticated, user]);

  // Clear cart when user logs out
  useEffect(() => {
    if (!isAuthenticated && !isSyncing) {
      // User logged out, clear cart items
      setCartItems([]);
    }
  }, [isAuthenticated, isSyncing]);

  // Add item to cart
  const addItem = async (product, quantity = 1) => {
    // Prevent adding products with invalid price (0.00)
    const price = Number(product.price) || 0;
    if (price <= 0) {
      console.error('Cannot add product with price 0.00 to cart:', product);
      throw new Error('Invalid product price. Please refresh the page and try again.');
    }
    
    if (isAuthenticated) {
      // Add to backend cart
      try {
        // Build cart item with only defined values
        const cartItem = {
          product_id: product.id,
          quantity,
          price: price,
          name: product.name || '',
        };
        
        // Add optional fields only if they exist
        const imageUrl = product.image_url || product.imageUrl || product.images?.[0];
        if (imageUrl) cartItem.image_url = imageUrl;
        
        if (product.cartId) cartItem.cart_id = product.cartId;
        if (product.subcategory) cartItem.subcategory = product.subcategory;
        if (product.subcategoryName || product.subcategory_name) {
          cartItem.subcategory_name = product.subcategoryName || product.subcategory_name;
        }
        
        // Add selected components if they exist
        if (product.selected_components && Array.isArray(product.selected_components)) {
          cartItem.selected_components = product.selected_components.map(comp => ({
            id: comp.id,
            name: comp.name,
            price: Number(comp.price) || 0,
            variation_id: comp.variation_id || null,
            variation_name: comp.variation_name || null
          }));
        }
        
        // Add base price without discount if it exists
        if (product.base_price_without_discount !== undefined && product.base_price_without_discount !== null) {
          cartItem.base_price_without_discount = Number(product.base_price_without_discount);
        }
        
        const updatedCart = await api.cart.addItem(cartItem);
        setCartItems(updatedCart.items);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error adding item to cart:', err);
        }
        // The server turned it down (e.g. it's no longer available): say why
        // instead of adding it anyway.
        if (err.response && err.response.status < 500) {
          const detail = err.response.data?.detail;
          throw new Error(typeof detail === 'string' ? detail : 'This item couldn’t be added to your cart.');
        }
        // Otherwise (offline, server error) fall back to the local cart
        addItemLocally(product, quantity);
      }
    } else {
      // Add to local cart
      addItemLocally(product, quantity);
    }
    
    // Automatically open the cart drawer when an item is added
    setIsOpen(true);
  };

  // Helper function to add item locally
  const addItemLocally = (product, quantity = 1) => {
    // Double-check price validation for local cart
    const price = Number(product.price) || 0;
    if (price <= 0) {
      console.error('Cannot add product with price 0.00 to local cart:', product);
      return;
    }
    
    setCartItems((prevItems) => {
      // Use cartId for subcategory variants, otherwise use product id
      const itemId = product.cartId || product.id;
      const existingItem = prevItems.find(item => (item.cartId || item.id) === itemId);
      
      if (existingItem) {
        // Update quantity if item already exists
        return prevItems.map(item =>
          (item.cartId || item.id) === itemId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Add new item
        return [...prevItems, { 
          ...product, 
          quantity,
          addedAt: new Date().toISOString()
        }];
      }
    });
  };

  // Remove item from cart
  const removeItem = async (productId) => {
    if (isAuthenticated) {
      // Remove from backend cart
      try {
        const updatedCart = await api.cart.removeItem(productId);
        setCartItems(updatedCart.items);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error removing item from cart:', err);
        }
        // Fall back to local removal on error
        removeItemLocally(productId);
      }
    } else {
      // Remove from local cart
      removeItemLocally(productId);
    }
  };

  // Helper function to remove item locally
  const removeItemLocally = (productId) => {
    setCartItems((prevItems) => prevItems.filter(item => (item.cartId || item.id || item.product_id) !== productId));
  };

  // Update item quantity
  const updateQuantity = async (productId, quantity) => {
    if (quantity <= 0) {
      await removeItem(productId);
      return;
    }
    
    if (isAuthenticated) {
      // Update in backend cart
      try {
        const updatedCart = await api.cart.updateItem(productId, quantity);
        // Recalculate prices with quantity discounts
        await recalculateCartPrices();
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error updating cart item:', err);
        }
        // Fall back to local update on error
        updateQuantityLocally(productId, quantity);
      }
    } else {
      // Update in local cart and recalculate prices
      updateQuantityLocally(productId, quantity);
      await recalculateLocalCartPrices();
    }
  };

  // Helper function to update quantity locally
  const updateQuantityLocally = (productId, quantity) => {
    setCartItems((prevItems) =>
      prevItems.map(item =>
        (item.cartId || item.id || item.product_id) === productId
          ? { ...item, quantity }
          : item
      )
    );
  };

  // Clear entire cart
  const clearCart = async () => {
    if (isAuthenticated) {
      // Clear backend cart
      try {
        await api.cart.clear();
        setCartItems([]);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error clearing cart:', err);
        }
        // Fall back to local clear on error
        setCartItems([]);
      }
    } else {
      // Clear local cart
      setCartItems([]);
    }
  };

  // Toggle cart drawer
  const toggleCart = () => {
    if (!isOpen) recheckAvailability();
    setIsOpen(!isOpen);
  };

  const openCart = () => {
    recheckAvailability();
    setIsOpen(true);
  };
  const closeCart = () => setIsOpen(false);

  // Calculate totals
  const getItemCount = () => {
    return cartItems.reduce((total, item) => total + (item.quantity || 0), 0);
  };

  const getSubtotal = () => {
    return cartItems.reduce((total, item) => {
      const price = Number(item.price) || 0;
      const quantity = Number(item.quantity) || 0;
      return total + (price * quantity);
    }, 0);
  };

  // Refresh cart from backend
  const refreshCart = async () => {
    if (isAuthenticated) {
      try {
        const serverCart = await api.cart.get();
        setCartItems(serverCart.items);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error refreshing cart:', err);
        }
      }
    }
  };

  // Recalculate cart prices with quantity discounts (backend)
  const recalculateCartPrices = async () => {
    if (isAuthenticated) {
      try {
        const updatedCart = await api.cart.recalculatePrices();
        setCartItems(updatedCart.items);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error recalculating cart prices:', err);
        }
      }
    }
  };

  // Recalculate local cart prices with quantity discounts and sale
  const recalculateLocalCartPrices = async () => {
    if (!isAuthenticated) {
      try {
        const pricingTier = user?.pricing_tier || 'retail';
        const updatedItems = await Promise.all(
          cartItems.map(async (item) => {
            try {
              const productId = item.product_id || item.id;
              const product = await api.products.getDetail(productId);
              
              let tierPrices = product.tier_prices;
              let saleInfo = null;
              
              if (item.subcategory && product.subcategories) {
                const variant = product.subcategories.find(s => s.id === item.subcategory);
                if (variant) {
                  if (variant.tier_prices) {
                    tierPrices = {
                      retail: variant.tier_prices.retail || product.tier_prices?.retail,
                      wholesale: variant.tier_prices.wholesale || product.tier_prices?.wholesale,
                      volume: variant.tier_prices.volume || product.tier_prices?.volume,
                      quantity_discounts: variant.tier_prices.quantity_discounts || product.tier_prices?.quantity_discounts
                    };
                  }
                  // Check variant-level sale first
                  if (variant.sale_discount_percent) {
                    saleInfo = {
                      sale_discount_percent: variant.sale_discount_percent,
                      sale_start_date: variant.sale_start_date,
                      sale_end_date: variant.sale_end_date,
                      sale_applicable_tiers: variant.sale_applicable_tiers
                    };
                  }
                }
              }
              
              // Fall back to product-level sale
              if (!saleInfo && product.sale_discount_percent) {
                saleInfo = {
                  sale_discount_percent: product.sale_discount_percent,
                  sale_start_date: product.sale_start_date,
                  sale_end_date: product.sale_end_date,
                  sale_applicable_tiers: product.sale_applicable_tiers
                };
              }
              
              const newPrice = calculatePriceWithQuantityDiscount(
                tierPrices,
                item.quantity,
                pricingTier,
                saleInfo
              );
              
              return {
                ...item,
                price: newPrice
              };
            } catch (err) {
              return item;
            }
          })
        );
        
        setCartItems(updatedItems);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error recalculating local cart prices:', err);
        }
      }
    }
  };

  // Lines that can't be bought as they are, with the server's reason.
  const unavailableItems = cartItems
    .map((item) => ({ item, key: cartItemKey(item), status: availability[cartItemKey(item)] }))
    .filter(({ status }) => status && !status.available);

  const value = {
    cartItems,
    availability,
    unavailableItems,
    hasUnavailableItems: unavailableItems.length > 0,
    recheckAvailability,
    isOpen,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    toggleCart,
    openCart,
    closeCart,
    refreshCart,
    itemCount: getItemCount(),
    subtotal: getSubtotal(),
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;

